/**
 * MultiSelectDropdown Component
 * 다중 선택 가능한 드롭다운 컴포넌트
 */
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './MultiSelectDropdown.css';

export interface MultiSelectOption {
    value: string;
    label: string;
    description?: string;
}

interface MultiSelectDropdownProps {
    options: MultiSelectOption[];
    selectedValues: string[];
    onChange: (values: string[]) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
}

interface DropdownPosition {
    top: number;
    left: number;
    width: number;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
    options,
    selectedValues,
    onChange,
    placeholder = '선택하세요...',
    className = '',
    disabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition>({ top: 0, left: 0, width: 0 });
    const containerRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    // 드롭다운 위치 계산
    const updateDropdownPosition = () => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + window.scrollY + 4,
                left: rect.left + window.scrollX,
                width: rect.width
            });
        }
    };

    // 드롭다운이 열릴 때 위치 계산
    useEffect(() => {
        if (isOpen) {
            updateDropdownPosition();
            window.addEventListener('scroll', updateDropdownPosition, true);
            window.addEventListener('resize', updateDropdownPosition);
            return () => {
                window.removeEventListener('scroll', updateDropdownPosition, true);
                window.removeEventListener('resize', updateDropdownPosition);
            };
        }
    }, [isOpen]);

    // 외부 클릭 감지
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                containerRef.current &&
                !containerRef.current.contains(target) &&
                listRef.current &&
                !listRef.current.contains(target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // 옵션 토글
    const handleToggle = (value: string) => {
        const newValues = selectedValues.includes(value)
            ? selectedValues.filter(v => v !== value)
            : [...selectedValues, value].sort();
        onChange(newValues);
    };

    // 전체 선택/해제
    const handleSelectAll = () => {
        if (selectedValues.length === options.length) {
            onChange([]);
        } else {
            onChange(options.map(opt => opt.value));
        }
    };

    // 표시 텍스트
    const getDisplayText = () => {
        if (selectedValues.length === 0) return placeholder;
        if (selectedValues.length === 1) {
            const selected = options.find(opt => opt.value === selectedValues[0]);
            return selected?.label || '';
        }
        return `${selectedValues.length}개 선택됨`;
    };

    // 드롭다운 렌더링
    const renderDropdown = () => {
        if (!isOpen) return null;

        const dropdown = (
            <div
                ref={listRef}
                className="multi-select-dropdown-portal"
                style={{
                    position: 'fixed',
                    top: dropdownPosition.top - window.scrollY,
                    left: dropdownPosition.left,
                    width: dropdownPosition.width
                }}
            >
                {/* 옵션 리스트 */}
                <div className="multi-select-options">
                    {options.map((option) => (
                        <label
                            key={option.value}
                            className={`multi-select-option ${selectedValues.includes(option.value) ? 'selected' : ''}`}
                        >
                            <input
                                type="checkbox"
                                checked={selectedValues.includes(option.value)}
                                onChange={() => handleToggle(option.value)}
                                className="multi-select-checkbox"
                            />
                            <div className="multi-select-option-content">
                                <span className="multi-select-option-label">{option.label}</span>
                                {option.description && (
                                    <span className="multi-select-option-description">
                                        {option.description}
                                    </span>
                                )}
                            </div>
                        </label>
                    ))}
                </div>

                {/* 전체 선택 버튼 */}
                <div className="multi-select-footer">
                    <button
                        type="button"
                        className="multi-select-all-btn"
                        onClick={handleSelectAll}
                    >
                        {selectedValues.length === options.length ? '전체 해제' : '전체 선택'}
                    </button>
                </div>
            </div>
        );

        return createPortal(dropdown, document.body);
    };

    return (
        <div
            ref={containerRef}
            className={`multi-select-dropdown ${className} ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
        >
            <button
                type="button"
                className="multi-select-trigger"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
            >
                <span className="multi-select-display">{getDisplayText()}</span>
                <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
            </button>

            {renderDropdown()}
        </div>
    );
};

export default MultiSelectDropdown;
